// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * One way in.
 *
 * A drop, a tap on the file picker, a paste, and "Try a sample" all arrive
 * here. There is deliberately no sample-only path: a demo that runs different
 * code from the real thing demonstrates nothing.
 *
 * The container is decided by MAGIC BYTES, never by the extension or by
 * `File.type`. A PNG that arrived through a chat app is routinely named .jpg,
 * and refusing it on the strength of its name would fail the exact file this
 * tool exists for. (ComfyUI's own reader does dispatch on MIME type, which is
 * why the report tells the user to rename the file — but that is a fact about
 * ComfyUI, not a rule this tool should copy.)
 */

export type Container = 'png' | 'jpeg' | 'webp' | 'gif' | 'unknown';

const MAGIC: Array<[Container, number[]]> = [
  ['png', [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  ['jpeg', [0xff, 0xd8, 0xff]],
  ['gif', [0x47, 0x49, 0x46, 0x38]],
];

export function sniffContainer(bytes: Uint8Array): Container {
  for (const [container, magic] of MAGIC) {
    if (bytes.length < magic.length) continue;
    if (magic.every((b, i) => bytes[i] === b)) return container;
  }
  // RIFF....WEBP
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return 'webp';
  }
  return 'unknown';
}

export function containerRefusal(container: Container): string | null {
  if (container === 'png') return null;
  if (container === 'jpeg') {
    return 'That is a JPEG. regraft reads PNG only: a JPEG cannot carry PNG text chunks, and re-saving as a JPEG is what destroyed the recipe in the first place.';
  }
  if (container === 'webp') {
    return 'That is a WebP. regraft reads PNG only for now — WebP stores a recipe in an EXIF block inside a RIFF container, which is a different job.';
  }
  if (container === 'gif') {
    return 'That is a GIF, which has nowhere to put a generation recipe.';
  }
  return 'That file does not begin with a PNG signature, so it is not a PNG whatever it is called.';
}

export const MAX_BYTES = 512 * 1024 * 1024;

export async function readFileToBytes(file: File): Promise<Uint8Array> {
  if (file.size === 0) throw new Error('That file is empty.');
  if (file.size > MAX_BYTES) {
    throw new Error(`That file is ${(file.size / 1e6).toFixed(0)} MB. regraft stops at ${MAX_BYTES / 1e6} MB.`);
  }
  return new Uint8Array(await file.arrayBuffer());
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
